import {
  detectCardType,
  formatCardNumber,
  generateArray,
  getCvvMaxLength,
  getNYearsAhead,
  mapSelectInputData,
  validateCardExpirationDate,
  validateCardNumber,
  validateHolderName,
} from '@openeducation/pp-js-utils';
import {
  cardHolderNameAtom,
  pmCountryAtom,
  selectedPaymentMethodAtom,
} from '@openeducation/pp-rn-jotai-atoms';
import { validateTwoCreditCardsRequirements } from '@openeducation/pp-rn-services';
import {
  CommonPaymentInfo,
  Country,
  CreditCard,
  CreditCardPaymentInfo,
  Currency,
  ExtendedPaymentInfoFormData,
  Organization,
  PaymentFlowType,
  PaymentInfoProps,
  PaymentMethod,
  SelectInputItem,
  Subscription,
  TinyPayloads,
} from '@openeducation/pp-rn-shared-types';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useInstallmentsInput } from './checkout/use-installments-input';
import { usePaymentMethodOnFile } from './checkout/use-payment-method-on-file';
import {
  calculateInstallmentsAmounts,
  getTotalAmountForInstallments,
} from '../helpers';

interface InvoiceDetail {
  amount: number;
  currencyIso: Currency;
  organization: Organization;
}

interface UsePaymentInfoProps {
  countryISOCode?: Country['isoCode'];
  allCountries: Country[];
  commonPaymentInfo?: CommonPaymentInfo;
  creditCardPaymentInfo?: CreditCardPaymentInfo;
  tinyPayloads?: TinyPayloads; // only available from checkout screen
  subscription?: Subscription;
  paypalEmail?: string;
  creditCardNumber?: string;
  splitInvoice?: boolean;
  invoiceDetail?: InvoiceDetail; // only available from invoice screen
}

const now = new Date();

export const usePaymentInfoProps = ({
  countryISOCode,
  commonPaymentInfo,
  allCountries,
  creditCardPaymentInfo,
  tinyPayloads,
  subscription,
  paypalEmail,
  creditCardNumber,
  splitInvoice = false,
  invoiceDetail,
}: UsePaymentInfoProps) => {
  const { t } = useTranslation();
  const paymentMethods = commonPaymentInfo?.paymentMethods;
  const cardTypes = creditCardPaymentInfo?.types;
  const setPmCountry = useSetAtom(pmCountryAtom);
  const setCardHolderName = useSetAtom(cardHolderNameAtom);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useAtom(
    selectedPaymentMethodAtom
  );

  const [formData, setFormData] = useState<ExtendedPaymentInfoFormData>({
    country: '',
    paymentMethod: '' as PaymentMethod,
    card1_cardType: '' as CreditCard,
    card1_cardNumber: '',
    card1_expirationMonth: '',
    card1_expirationYear: '',
    card1_holderName: '',
    card1_cvv: '',
    card1_installments: '',
    card1_amount: '',
    card1_billingAddress_address: '',
    card1_billingAddress_number: '',
    card1_billingAddress_complement: '',
    card1_billingAddress_city: '',
    card1_billingAddress_state: '',
    card1_billingAddress_postcode: '',
    card1_billingAddress_country: '',
    card1_documentNumber: '',
    card1_documentType: '',
    card2_cardType: '' as CreditCard,
    card2_cardNumber: '',
    card2_expirationMonth: '',
    card2_expirationYear: '',
    card2_holderName: '',
    card2_cvv: '',
    card2_installments: '',
    card2_amount: '',
    card2_billingAddress_address: '',
    card2_billingAddress_number: '',
    card2_billingAddress_complement: '',
    card2_billingAddress_city: '',
    card2_billingAddress_state: '',
    card2_billingAddress_postcode: '',
    card2_billingAddress_country: '',
    card2_documentNumber: '',
    card2_documentType: '',
  });

  const isCreditCard = formData.paymentMethod === PaymentMethod.CREDIT_CARD;
  const isTwoCreditCards =
    formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS;
  const isPayPal = formData.paymentMethod === PaymentMethod.PAY_PAL;

  const paymentAmount =
    (tinyPayloads
      ? getTotalAmountForInstallments(tinyPayloads)
      : invoiceDetail?.amount) ?? 0;

  const { card1AmountForInstallments, card2AmountForInstallments } =
    calculateInstallmentsAmounts({
      paymentMethod: formData.paymentMethod,
      totalAmount: paymentAmount,
      card1Amount: formData.card1_amount,
    });

  const twentyYearsAhead = getNYearsAhead(now, 20, 'yy');
  const months = generateArray(12).map((index: number) => `${index + 1}`);

  const { paymentMethodOnFile, isExistingPaymentMethod } =
    usePaymentMethodOnFile({
      subscription,
      isUpsell: tinyPayloads?.paymentFlowType === PaymentFlowType.UPSELL,
      setFormData,
    });

  const detectedCard1Type = useMemo(() => {
    if (!formData.card1_cardNumber) {
      return null;
    }
    return detectCardType(formData.card1_cardNumber);
  }, [formData.card1_cardNumber]);
  const isCard1AmericanExpress =
    detectedCard1Type === CreditCard.AMERICAN_EXPRESS;

  const detectedCard2Type = useMemo(() => {
    if (!formData.card2_cardNumber) {
      return null;
    }
    return detectCardType(formData.card2_cardNumber);
  }, [formData.card2_cardNumber]);
  const isCard2AmericanExpress =
    detectedCard2Type === CreditCard.AMERICAN_EXPRESS;

  useEffect(() => {
    setCardHolderName(formData.card1_holderName);
  }, [setCardHolderName, formData.card1_holderName]);

  useEffect(() => {
    setPmCountry(formData.country);
  }, [setPmCountry, formData.country]);

  useEffect(() => {
    if (paymentMethods && tinyPayloads) {
      validateTwoCreditCardsRequirements(paymentMethods, tinyPayloads);
    }
  }, [paymentMethods, tinyPayloads]);

  const handleFieldChange = useCallback(
    (value: string, name?: string) => {
      if (name) {
        let newValue = value;
        if (name === 'paymentMethod') {
          setSelectedPaymentMethod(value as PaymentMethod);
        }

        if (name === 'card1_cardNumber') {
          newValue = formatCardNumber(value, isCard1AmericanExpress);
        } else if (name === 'card2_cardNumber') {
          newValue = formatCardNumber(value, isCard2AmericanExpress);
        } else if (name === 'card1_cvv' || name === 'card2_cvv') {
          newValue = value.replace(/[^0-9]/g, '');
        }

        setFormData((prev) => ({ ...prev, [name]: newValue }));
      }
    },
    [isCard1AmericanExpress, isCard2AmericanExpress, setSelectedPaymentMethod]
  );

  const paymentCurrency =
    tinyPayloads?.currencyISOCode ?? invoiceDetail?.currencyIso;
  const paymentOrganization =
    tinyPayloads?.organization ?? invoiceDetail?.organization;

  // isB2B is only true for B2B DS, not alliances (B2B2C).
  const isB2B = !!invoiceDetail && !tinyPayloads;

  const { installmentsInputData: installmentsInputData1 } =
    useInstallmentsInput({
      installmentsFormData: formData.card1_installments,
      handleFieldChange,
      fieldName: 'card1_installments',
      cardType: formData.card1_cardType,
      organization: paymentOrganization,
      currency: paymentCurrency,
      billingFrequency: tinyPayloads?.billingFrequency,
      amount: card1AmountForInstallments,
      initialTerm: tinyPayloads?.initialTerm,
      cardBinNumber: formData.card1_cardNumber.replaceAll(' ', '').slice(0, 6),
      splitInvoice,
      isB2B,
    });

  const { installmentsInputData: installmentsInputData2 } =
    useInstallmentsInput({
      installmentsFormData: formData.card2_installments || '',
      handleFieldChange,
      fieldName: 'card2_installments',
      cardType: formData.card2_cardType,
      organization: paymentOrganization,
      currency: paymentCurrency,
      billingFrequency: tinyPayloads?.billingFrequency,
      amount: card2AmountForInstallments,
      initialTerm: tinyPayloads?.initialTerm,
      cardBinNumber:
        formData.card2_cardNumber?.replaceAll(' ', '').slice(0, 6) || '',
      splitInvoice,
      isB2B,
    });

  useEffect(() => {
    if (!formData.card1_cardType && detectedCard1Type) {
      handleFieldChange(detectedCard1Type, 'card1_cardType');
    }
    if (!formData.card2_cardType && detectedCard2Type) {
      handleFieldChange(detectedCard2Type, 'card2_cardType');
    }
  }, [
    detectedCard1Type,
    formData.card1_cardType,
    detectedCard2Type,
    formData.card2_cardType,
    handleFieldChange,
  ]);

  useEffect(() => {
    if (countryISOCode) {
      handleFieldChange(countryISOCode, 'country');
    }
  }, [countryISOCode, handleFieldChange]);

  useEffect(() => {
    if (creditCardNumber) {
      handleFieldChange(creditCardNumber, 'card1_cardNumber');
    }
  }, [creditCardNumber, handleFieldChange]);

  useEffect(() => {
    if (selectedPaymentMethod !== formData.paymentMethod) {
      handleFieldChange(selectedPaymentMethod as string, 'paymentMethod');
    }
  }, [selectedPaymentMethod, formData.paymentMethod, handleFieldChange]);

  const fields = useMemo(
    () => ({
      isCountryEnabled: !!commonPaymentInfo?.country,
      isCountryFixed: !!commonPaymentInfo?.isCountryFixed,
      isCardTypeEnabled: isCreditCard || isTwoCreditCards,
      isCardNumberEnabled:
        (isCreditCard || isTwoCreditCards) && !!creditCardPaymentInfo?.number,
      isExpirationDateEnabled:
        (isCreditCard || isTwoCreditCards) &&
        !!creditCardPaymentInfo?.expirationDate,
      isHolderNameEnabled:
        (isCreditCard || isTwoCreditCards) &&
        !!creditCardPaymentInfo?.holderName,
      isCvvEnabled:
        (isCreditCard || isTwoCreditCards) && !!creditCardPaymentInfo?.cvv,
      isInstallmentsEnabled:
        installmentsInputData1.length > 0 ||
        (isTwoCreditCards && installmentsInputData2.length > 0),
      isExistingPaymentMethod,
      isPayPalEmailEnabled: isPayPal && isExistingPaymentMethod,
      isDraftCreditCardNumber: !!creditCardNumber,
    }),
    [
      commonPaymentInfo,
      creditCardPaymentInfo,
      installmentsInputData1,
      installmentsInputData2,
      isExistingPaymentMethod,
      isCreditCard,
      isTwoCreditCards,
      isPayPal,
      creditCardNumber,
    ]
  );

  const validation = useMemo(() => {
    const card1Amount = parseFloat(formData.card1_amount) || 0;
    const minPercentage = tinyPayloads?.mainPaymentMinPercentage || 0;
    const minAmount = (paymentAmount * minPercentage) / 100;

    const isCard2NumberValid =
      formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS
        ? formData.card2_cardNumber
          ? validateCardNumber(formData.card2_cardNumber)
          : false
        : true;

    const isCard2ExpirationValid =
      formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS
        ? formData.card2_expirationMonth && formData.card2_expirationYear
          ? validateCardExpirationDate(
              now,
              formData.card2_expirationMonth,
              formData.card2_expirationYear
            )
          : false
        : true;

    const isCard2HolderNameValid =
      formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS
        ? formData.card2_holderName
          ? validateHolderName(formData.card2_holderName)
          : false
        : true;

    const isDraftCreditCardNumber = !!creditCardNumber;

    const isCard1CvvValid = (() => {
      if (!formData.card1_cvv) {
        return false;
      }
      const expectedLength = getCvvMaxLength(formData.card1_cardType);
      return formData.card1_cvv.length === expectedLength;
    })();

    const isCard2CvvValid = (() => {
      if (formData.paymentMethod !== PaymentMethod.TWO_CREDIT_CARDS) {
        return true;
      }
      if (!formData.card2_cvv) {
        return false;
      }
      const expectedLength = getCvvMaxLength(formData.card2_cardType);
      return formData.card2_cvv.length === expectedLength;
    })();

    return {
      isCard1TypeValid: cardTypes
        ? cardTypes.includes(formData.card1_cardType)
        : false,
      isCard1NumberValid:
        (isExistingPaymentMethod &&
          formData.paymentMethod === PaymentMethod.CREDIT_CARD) ||
        (isDraftCreditCardNumber &&
          formData.paymentMethod === PaymentMethod.CREDIT_CARD) ||
        validateCardNumber(formData.card1_cardNumber),
      isCard1ExpirationValid: validateCardExpirationDate(
        now,
        formData.card1_expirationMonth,
        formData.card1_expirationYear
      ),
      isCard1HolderNameValid: validateHolderName(formData.card1_holderName),
      isCard1CvvValid,
      isCard1AmountValid:
        formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS
          ? card1Amount >= minAmount && card1Amount < paymentAmount
          : true,
      isCard2TypeValid:
        formData.paymentMethod === PaymentMethod.TWO_CREDIT_CARDS
          ? cardTypes && formData.card2_cardType
            ? cardTypes.includes(formData.card2_cardType)
            : false
          : true,
      isCard2NumberValid,
      isCard2ExpirationValid,
      isCard2HolderNameValid,
      isCard2CvvValid,
    };
  }, [
    cardTypes,
    formData.card1_cardType,
    formData.card1_cardNumber,
    formData.card1_expirationMonth,
    formData.card1_expirationYear,
    formData.card1_holderName,
    formData.card1_cvv,
    formData.card1_amount,
    formData.card2_cardType,
    formData.card2_cardNumber,
    formData.card2_expirationMonth,
    formData.card2_expirationYear,
    formData.card2_holderName,
    formData.card2_cvv,
    formData.paymentMethod,
    isExistingPaymentMethod,
    creditCardNumber,
    paymentAmount,
    tinyPayloads?.mainPaymentMinPercentage,
  ]);

  const paymentInfoProps = {
    formData,
    selectData: {
      country: mapSelectInputData<Country>(
        allCountries,
        'isoCode',
        'isoCode'
      ) as SelectInputItem[],
      paymentMethod:
        paymentMethods?.map((item) => {
          return {
            label: t(`ppui.payment_info.method.${item.toLocaleLowerCase()}`),
            value: item,
          } as SelectInputItem;
        }) || [],
      cardType: mapSelectInputData<CreditCard>(
        creditCardPaymentInfo?.types || []
      ),
      expirationMonth: mapSelectInputData<string>(months),
      expirationYear: mapSelectInputData<string>(twentyYearsAhead),
      installments1: installmentsInputData1,
      installments2: installmentsInputData2,
    },
    onFieldChange: handleFieldChange,
    fields,
    validation,
    paymentMethodOnFile,
    paypalEmail: paypalEmail || subscription?.paypalEmail,
    totalAmount: paymentAmount,
    currency: paymentCurrency,
    mainPaymentMinPercentage: (tinyPayloads as any)?.mainPaymentMinPercentage,
  } as Omit<PaymentInfoProps, 'renderCardPreview'>;

  return { paymentInfoProps };
};
